import {CaseContext} from '../../../../../redux/slice/aiReviewSliceV2';
import {CaseContextFieldKey} from './caseContextConfig';

/**
 * Mock Case Context payload aligned with the API `caseContext` contract
 * and `CaseContext` / `PartyRole` types used by `useProtocolCaseContextState`.
 * Use this to drive Protocol Case Context in view mode until the API is wired.
 */
export const MOCK_CASE_CONTEXT_VIEW: CaseContext = {
  executive_summary:
    'This matter concerns alleged breaches of fiduciary duty and misappropriation of trade secrets by a former executive who joined a competitor. The review prioritizes communications about product roadmaps, customer lists, and solicitation of key employees between January 2022 and March 2024.',
  parties_and_roles: [
    {
      party: 'Acme Technologies, Inc.',
      role: 'Plaintiff',
      party_description:
        'Software company alleging misappropriation of proprietary algorithms and customer data.',
    },
    {
      party: 'Jordan Hale',
      role: 'Defendant / Former VP Engineering',
      party_description:
        'Former Acme executive who joined rival Orion Labs in February 2023.',
    },
    {
      party: 'Orion Labs LLC',
      role: 'Defendant / Competitor',
      party_description:
        'Competing analytics vendor alleged to have benefitted from Acme confidential information.',
    },
  ],
  claims: [
    'Misappropriation of trade secrets under the Defend Trade Secrets Act',
    'Breach of fiduciary duty',
    'Breach of employee confidentiality and non-solicitation agreements',
  ],
  defenses: [
    'Independent development of competing product features',
    'Information alleged as trade secrets was publicly available',
    'No causal link between defendant conduct and alleged damages',
  ],
  disputed_facts: [
    'Whether customer pricing models qualify as trade secrets',
    'Whether Hale retained access to Acme systems after resignation',
    'Scope of solicitation of Acme engineers by Orion Labs',
  ],
  key_legal_and_factual_issues: [
    'Ownership and protectability of product roadmap materials',
    'Timing of alleged downloads relative to resignation date',
    'Use of Acme customer lists in Orion Labs sales outreach',
  ],
  primary_date_range: '2022-01-01 to 2024-03-31',
  important_subperiods: [
    '2022-06 to 2022-12: Product X roadmap finalized',
    '2023-01 to 2023-02: Hale resignation and transition',
    '2023-03 to 2023-09: Alleged solicitation window',
  ],
  custodians: [],
  departments: ['Engineering', 'Sales', 'Human Resources', 'Legal'],
  systems_and_repositories: [
    'Microsoft 365 (Exchange / OneDrive / Teams)',
    'Slack Enterprise Grid',
    'GitHub Enterprise',
    'Salesforce CRM',
  ],
  responsiveness_standard_definition:
    'A document is responsive if it discusses Acme confidential technical information, customer lists, pricing strategy, solicitation of Acme employees, or communications between Hale and Orion Labs regarding Product X or competing offerings during the primary date range.',
  responsive_examples: [
    'Email attaching Acme customer pricing spreadsheet to a personal account',
    'Slack thread discussing copying Acme roadmap items into Orion Labs backlog',
    'Calendar invite for Orion Labs interview with Acme engineer',
  ],
  non_responsive_examples: [
    'Routine HR benefits enrollment notices',
    'Public marketing newsletters with no confidential content',
    'Personal calendar entries unrelated to work duties',
  ],
  relevancy_keywords: [
    'trade secret',
    'roadmap',
    'customer list',
    'non-solicit',
    'Orion Labs',
    'Product X',
  ],
  relevance_themes: [
    'Transfer of confidential technical materials',
    'Employee solicitation and recruiting',
    'Competitive product development overlap',
  ],
  behavioral_indicators: [
    'Large file downloads shortly before resignation',
    'Use of personal email for work attachments',
    'After-hours access to restricted repositories',
  ],
  decision_making_indicators: [
    'Approvals to share roadmap decks externally',
    'Direction to recruit specific Acme engineers',
    'Instructions to sanitize origin of competing features',
  ],
  explicit_exclusions: [
    'Documents solely about publicly filed court pleadings',
    'Purely personal communications with no business content',
    'Third-party vendor invoices with no case nexus',
  ],
  reviewer_instructions: [
    'Flag any attachment that appears to be an Acme customer export',
    'Code family members together when parent and attachment differ in responsiveness',
    'Escalate documents that reference attorney advice for privilege review',
  ],
  borderline_scenarios: [
    'Public conference slides that closely mirror internal roadmap wording',
    'Recruiting messages that do not name Acme but target known Acme engineers',
  ],
  ambiguities_and_known_limits: [
    'Slack retention may be incomplete before mid-2022',
    'Personal devices used by Hale were not imaged',
    'Some Salesforce export fields lack message body content',
  ],
};

/** View-mode ambiguity banners shown via ArrSectionCard / ArrBanner */
export const MOCK_CASE_CONTEXT_VIEW_BANNERS: Partial<
  Record<CaseContextFieldKey, string>
> = {
  custodians:
    'Ambiguity: Specific individual custodians are not explicitly named in the protocol, other than the list of attorneys in Appendix A. Reviewers should expect documents from employees involved in Trade Compliance, manufacturing, and supply chain.',
  systems_and_repositories:
    'Ambiguity: Specific data collection sources (e.g., Outlook, Teams, local drives) are not explicitly defined in the protocol.',
};
